import axiosInstance from '@/services/axiosInstance'

const state = {
  subscription: {
    hasSubscription: false,
    status: null,
    currentPeriodEnd: null,
    cancelAtPeriodEnd: false,
    plan: null
  },
  invoices: [],
  orders: [],
  billingEmail: null,
  loading: false,
  error: null
}

const getters = {
  isSubscribed: state => state.subscription.hasSubscription,
  subscriptionStatus: state => state.subscription.status,
  currentPlan: state => state.subscription.plan,
  invoices: state => state.invoices,
  orders: state => state.orders,
  billingEmail: state => state.billingEmail,
  isLoading: state => state.loading,
  error: state => state.error
}

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  
  SET_ERROR(state, error) {
    state.error = error
  },
  
  SET_SUBSCRIPTION(state, subscription) {
    state.subscription = {
      ...state.subscription,
      ...subscription
    }
  },
  
  SET_INVOICES(state, invoices) {
    state.invoices = invoices
  },
  
  SET_ORDERS(state, orders) {
    state.orders = orders
  },
  
  SET_BILLING_EMAIL(state, email) {
    state.billingEmail = email
  },
  
  CLEAR_BILLING(state) {
    state.subscription = {
      hasSubscription: false,
      status: null,
      currentPeriodEnd: null,
      cancelAtPeriodEnd: false,
      plan: null
    }
    state.invoices = []
    state.orders = []
    state.billingEmail = null
    state.error = null
  }
}

const actions = {
  async fetchSubscription({ commit }) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      const { data } = await axiosInstance.get('/billing/subscription')
      commit('SET_SUBSCRIPTION', data)
      commit('SET_BILLING_EMAIL', data.billingEmail)
    } catch (error) {
      console.error('Error fetching subscription:', error)
      commit('SET_ERROR', error.response?.data?.message || 'Error al obtener suscripción')
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async fetchInvoices({ commit }) {
    try {
      commit('SET_LOADING', true)
      const { data } = await axiosInstance.get('/billing/invoices')
      commit('SET_INVOICES', data.invoices)
    } catch (error) {
      console.error('Error fetching invoices:', error)
      commit('SET_ERROR', error.response?.data?.message || 'Error al obtener facturas')
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async fetchOrders({ commit }) {
    try {
      commit('SET_LOADING', true)
      const { data } = await axiosInstance.get('/billing/orders')
      commit('SET_ORDERS', data.orders)
    } catch (error) {
      console.error('Error fetching orders:', error)
      commit('SET_ERROR', error.response?.data?.message || 'Error al obtener órdenes')
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async changePlan({ commit }, newPlanId) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      const { data } = await axiosInstance.post('/billing/change-plan', {
        newPlanId
      })
      
      // Una bajada programada no trae "subscription": la vista recarga los datos después
      if (data.subscription) commit('SET_SUBSCRIPTION', data.subscription)
      return data
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Error al cambiar plan'
      commit('SET_ERROR', errorMsg)
      throw new Error(errorMsg)
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async cancelSubscription({ commit }) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      const { data } = await axiosInstance.post('/billing/cancel')
      commit('SET_SUBSCRIPTION', data.subscription)
      return data
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Error al cancelar suscripción'
      commit('SET_ERROR', errorMsg)
      throw new Error(errorMsg)
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  // Deshace una cancelación que sigue vigente: no cobra nada hoy
  async reactivateSubscription({ commit }) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      const { data } = await axiosInstance.post('/billing/reactivate')
      return data
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'No se pudo reactivar el plan'
      commit('SET_ERROR', errorMsg)
      throw new Error(errorMsg)
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateBillingEmail({ commit }, billingEmail) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      const { data } = await axiosInstance.put('/billing/invoice-email', {
        billingEmail
      })
      
      commit('SET_BILLING_EMAIL', data.billingEmail)
      return data
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Error al actualizar email'
      commit('SET_ERROR', errorMsg)
      throw new Error(errorMsg)
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async downloadInvoice({ commit }, invoiceId) {
    try {
      commit('SET_LOADING', true)
      const { data } = await axiosInstance.post(`/billing/invoice/${invoiceId}/download`)
      return data
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Error al descargar factura'
      commit('SET_ERROR', errorMsg)
      throw new Error(errorMsg)
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  clearBilling({ commit }) {
    commit('CLEAR_BILLING')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
